import { ButtonProps, Grid } from '@chakra-ui/react';
import { DPYear, useDatePicker } from '@rehookify/datepicker';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import MButton from '../MButton';
import { MBox } from '../chakra';

const YEARS_PER_PAGE = 18;
const COLUMNS = 3;

interface YearViewProps {
  years: DPYear[];
  onChange: (year: DPYear) => void;
  yearBtnPropsFn: ReturnType<typeof useDatePicker>['propGetters']['yearButton'];
  onNavigateYearPage?: (direction: 'prev' | 'next') => void;
}

/**
 * Displays the years in a grid and allows user to select a year
 */
export const YearView = ({
  years,
  onChange,
  yearBtnPropsFn,
  onNavigateYearPage,
}: YearViewProps) => {
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [pendingFocusIndex, setPendingFocusIndex] = useState<number | null>(
    null,
  );

  const yearsWithDisabled = useMemo(
    () =>
      years.map((year) => ({
        year,
        isDisabled: !!yearBtnPropsFn(year).disabled,
      })),
    [years, yearBtnPropsFn],
  );

  const rovingIndex = useMemo(() => {
    const selectedIdx = yearsWithDisabled.findIndex(
      ({ year }) => year.selected,
    );
    if (selectedIdx >= 0 && !yearsWithDisabled[selectedIdx].isDisabled) {
      return selectedIdx;
    }
    const activeIdx = yearsWithDisabled.findIndex(({ year }) => year.now);
    if (activeIdx >= 0 && !yearsWithDisabled[activeIdx].isDisabled) {
      return activeIdx;
    }
    const firstEnabledIdx = yearsWithDisabled.findIndex(
      ({ isDisabled }) => !isDisabled,
    );
    return firstEnabledIdx >= 0 ? firstEnabledIdx : 0;
  }, [yearsWithDisabled]);

  // Focus the pending index after a year-page navigation re-render.
  // After a page change, the target year may be disabled by min/max
  // constraints — scan outward (forward, then backward) to the nearest
  // enabled year so keyboard focus is preserved, mirroring MonthView.
  useEffect(() => {
    if (pendingFocusIndex !== null) {
      let focusIndex: number | null = null;
      if (!yearsWithDisabled[pendingFocusIndex]?.isDisabled) {
        focusIndex = pendingFocusIndex;
      } else {
        for (let i = pendingFocusIndex + 1; i < YEARS_PER_PAGE; i += 1) {
          if (!yearsWithDisabled[i].isDisabled) {
            focusIndex = i;
            break;
          }
        }
        if (focusIndex === null) {
          for (let i = pendingFocusIndex - 1; i >= 0; i -= 1) {
            if (!yearsWithDisabled[i].isDisabled) {
              focusIndex = i;
              break;
            }
          }
        }
      }
      if (focusIndex !== null) {
        buttonRefs.current[focusIndex]?.focus();
      }
      setPendingFocusIndex(null);
    }
  }, [pendingFocusIndex, years, yearsWithDisabled]);

  const findNextEnabledIndex = useCallback(
    (startIndex: number, step: number): number | null => {
      let index = startIndex;
      while (index >= 0 && index < YEARS_PER_PAGE) {
        if (!yearsWithDisabled[index].isDisabled) {
          return index;
        }
        index += step;
      }
      return null;
    },
    [yearsWithDisabled],
  );

  const handleGridKeyDown = useCallback(
    (event: React.KeyboardEvent, currentIndex: number) => {
      let nextIndex: number | null = null;
      let step = 0;

      switch (event.key) {
        case 'ArrowRight':
          step = 1;
          nextIndex = currentIndex + 1;
          break;
        case 'ArrowLeft':
          step = -1;
          nextIndex = currentIndex - 1;
          break;
        case 'ArrowDown':
          step = COLUMNS;
          nextIndex = currentIndex + COLUMNS;
          break;
        case 'ArrowUp':
          step = -COLUMNS;
          nextIndex = currentIndex - COLUMNS;
          break;
        default:
          return;
      }

      event.preventDefault();

      if (nextIndex < 0) {
        if (onNavigateYearPage) {
          onNavigateYearPage('prev');
          setPendingFocusIndex(YEARS_PER_PAGE + nextIndex);
        }
        return;
      }

      if (nextIndex >= YEARS_PER_PAGE) {
        if (onNavigateYearPage) {
          onNavigateYearPage('next');
          setPendingFocusIndex(nextIndex - YEARS_PER_PAGE);
        }
        return;
      }

      const enabledIndex = findNextEnabledIndex(nextIndex, step);
      if (enabledIndex !== null) {
        buttonRefs.current[enabledIndex]?.focus();
      }
    },
    [findNextEnabledIndex, onNavigateYearPage],
  );

  return (
    <Grid
      gridTemplateColumns="repeat(3, minmax(0, 1fr))"
      rowGap="0.5rem"
      columnGap=".1rem"
      overflowY="auto"
    >
      {yearsWithDisabled.map(({ year, isDisabled }, index) => (
        <YearButton
          key={year.year}
          ref={(el) => {
            buttonRefs.current[index] = el;
          }}
          calendarYear={{
            ...year,
            disabled: isDisabled,
          }}
          {...yearBtnPropsFn(year)}
          disabled={isDisabled}
          tabIndex={index === rovingIndex ? 0 : -1}
          onClick={() => !isDisabled && onChange(year)}
          onGridKeyDown={(event) => handleGridKeyDown(event, index)}
        />
      ))}
    </Grid>
  );
};

interface YearButtonProps extends ButtonProps {
  calendarYear: DPYear;
  onGridKeyDown?: (event: React.KeyboardEvent) => void;
}

const YearButton = forwardRef<HTMLButtonElement, YearButtonProps>(
  ({ calendarYear, onGridKeyDown, ...rest }, ref) => {
    const { year, active, disabled, selected } = calendarYear;
    let buttonProps: ButtonProps = {};
    // On the span, not the button: see the note in DatePickerCalendarView.
    let textColor = disabled ? 'fg.muted' : 'fg.primary';

    if (selected) {
      textColor = 'fg.onAccent';
      buttonProps = {
        ...buttonProps,
        bg: 'tIndigo.base',
        _hover: {
          bg: 'tIndigo.base',
        },
        _focus: {
          bg: 'tIndigo.base',
        },
      };
    }

    if (active) {
      buttonProps = { ...buttonProps, border: '1px solid tGray.lightPurple' };
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)
      ) {
        onGridKeyDown?.(event);
      }
    };

    return (
      <MButton
        ref={ref}
        variant="tertiary"
        minW="unset"
        h="32px"
        m={1}
        isDisabled={disabled}
        fontWeight={400}
        borderRadius="3"
        _focus={{
          bg: 'inherit',
        }}
        _focusVisible={{
          boxShadow: 'focus',
        }}
        onKeyDown={handleKeyDown}
        {...buttonProps}
        {...rest}
      >
        <MBox as="span" color={textColor}>
          {year}
        </MBox>
      </MButton>
    );
  },
);
